import { hexToRgb, isHexColorDark, normalizeHexColor } from './image-style-prompt';

/** WCAG AA for body text. An image below this under the theme's text gets a veil. */
export const COVER_VEIL_MIN_CONTRAST = 4.5;
/** What a veil aims for. AA alone leaves a murky grey behind the text, so this is AAA. */
export const COVER_VEIL_TARGET_CONTRAST = 7;
/** The builder only recolors text for an overlay of at least this strength. */
export const COVER_VEIL_MIN_INTENSITY = 30;
/** Past this the image is gone, so a stronger veil adds nothing. */
export const COVER_VEIL_MAX_INTENSITY = 85;
export const COVER_VEIL_STEP = 5;
/** Width and height the image is reduced to before measuring. */
export const COVER_VEIL_SAMPLE_SIZE = { width: 48, height: 64 } as const;

export interface CoverVeil {
  /** Percent of the veil color laid over the image. */
  intensity: number;
  color: string;
}

/** An intensity the veil search can produce. The parser accepts nothing else from the server. */
export const isCoverVeilIntensity = (value: unknown): value is number =>
  typeof value === 'number' &&
  value >= COVER_VEIL_MIN_INTENSITY &&
  value <= COVER_VEIL_MAX_INTENSITY;

export interface CoverVeilTheme {
  heading?: string;
  body?: string;
  cardBackground?: string;
}

type Rgb = readonly [number, number, number];

const toLinear = (channel: number) => {
  const value = channel / 255;
  return value <= 0.03928 ? value / 12.92 : ((value + 0.055) / 1.055) ** 2.4;
};

const luminance = ([r, g, b]: Rgb) =>
  0.2126 * toLinear(r) + 0.7152 * toLinear(g) + 0.0722 * toLinear(b);

const contrastRatio = (a: Rgb, b: Rgb) => {
  const [lighter, darker] = [luminance(a), luminance(b)].sort((x, y) => y - x) as [number, number];
  return (lighter + 0.05) / (darker + 0.05);
};

const toRgb = (hex: string): Rgb => {
  const { r, g, b } = hexToRgb(hex);
  return [r, g, b];
};

/** WCAG contrast ratio between two normalized hex colors. */
export const getHexContrastRatio = (first: string, second: string) =>
  contrastRatio(toRgb(first), toRgb(second));

/** Lowest contrast of `text` against any pixel, with the image under a veil of `alpha` (0-1). */
const worstContrast = (pixels: Uint8Array, text: Rgb, veil: Rgb, alpha: number) => {
  let worst = Infinity;
  for (let i = 0; i + 2 < pixels.length; i += 3) {
    const blended: Rgb = [
      pixels[i]! * (1 - alpha) + veil[0] * alpha,
      pixels[i + 1]! * (1 - alpha) + veil[1] * alpha,
      pixels[i + 2]! * (1 - alpha) + veil[2] * alpha,
    ];
    worst = Math.min(worst, contrastRatio(text, blended));
  }
  return worst;
};

/**
 * Decides whether an untinted cover image needs a veil in the page color so the text stays
 * readable. `pixels` is the image reduced to RGB triplets.
 *
 * The check is against the worst pixel, not the average: the cover text can land anywhere, and
 * one dark patch under the title is enough to lose it. Returns null when the theme's own text
 * colors already read on every pixel, or when the theme has no usable card color to veil with.
 *
 * Once the veil is 30% or stronger the builder swaps the text to black or white, picked from the
 * veil color, so the strength is searched against that color rather than the theme's.
 */
export const computeCoverVeil = (pixels: Uint8Array, theme: CoverVeilTheme): CoverVeil | null => {
  const card = normalizeHexColor(theme.cardBackground);
  if (!card || pixels.length < 3) return null;

  const cardRgb = toRgb(card);
  const readableOnCard: Rgb = isHexColorDark(card) ? [255, 255, 255] : [0, 0, 0];
  const themeText = [theme.heading, theme.body].flatMap((color) => {
    const hex = normalizeHexColor(color);
    return hex ? [toRgb(hex)] : [];
  });
  const textColors = themeText.length > 0 ? themeText : [readableOnCard];

  const readsAsIs = textColors.every(
    (text) => worstContrast(pixels, text, cardRgb, 0) >= COVER_VEIL_MIN_CONTRAST,
  );
  if (readsAsIs) return null;

  for (
    let intensity = COVER_VEIL_MIN_INTENSITY;
    intensity < COVER_VEIL_MAX_INTENSITY;
    intensity += COVER_VEIL_STEP
  ) {
    if (
      worstContrast(pixels, readableOnCard, cardRgb, intensity / 100) >= COVER_VEIL_TARGET_CONTRAST
    ) {
      return { intensity, color: card };
    }
  }
  return { intensity: COVER_VEIL_MAX_INTENSITY, color: card };
};

/** Shape check for a veil that arrives on a stream frame. */
export const isCoverVeil = (value: unknown): value is CoverVeil => {
  if (typeof value !== 'object' || value === null) return false;
  const { intensity, color } = value as Record<string, unknown>;
  return (
    isCoverVeilIntensity(intensity) &&
    typeof color === 'string' &&
    normalizeHexColor(color) !== undefined
  );
};
